/**
 * Error Boundary Component
 * 
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI.
 * 
 * @version 2.0.0
 */

import React, { Component } from 'react';
import type { ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log error to error reporting service
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
    
    // TODO: Send to error monitoring service (Sentry)
    // if (env.SENTRY_DSN) {
    //   Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
    // }
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      // Default fallback UI
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
            padding: 'var(--space-8)',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              maxWidth: '600px',
              background: 'var(--bg-primary)',
              padding: 'var(--space-8)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-md)',
            }}
          >
            <h2
              style={{
                color: 'var(--color-error)',
                fontSize: 'var(--font-size-2xl)',
                fontWeight: 'var(--font-weight-semibold)',
                marginBottom: 'var(--space-4)',
              }}
            >
              Something went wrong
            </h2>
            
            <p
              style={{
                color: 'var(--text-secondary)',
                fontSize: 'var(--font-size-base)',
                marginBottom: 'var(--space-6)',
                lineHeight: 'var(--line-height-relaxed)',
              }}
            >
              We're sorry, but something unexpected happened. Please try refreshing the page or
              contact us if the problem persists.
            </p>
            
            {this.state.error && (
              <details
                style={{
                  marginBottom: 'var(--space-6)',
                  textAlign: 'left',
                  background: 'var(--bg-secondary)',
                  padding: 'var(--space-4)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <summary
                  style={{
                    cursor: 'pointer',
                    fontWeight: 'var(--font-weight-medium)',
                    marginBottom: 'var(--space-2)',
                  }}
                >
                  Error Details
                </summary>
                <pre
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-error)',
                    overflow: 'auto',
                    fontFamily: 'var(--font-family-mono)',
                  }}
                >
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
            
            <div style={{ display: 'flex', gap: 'var(--space-4)', justifyContent: 'center' }}>
              <button
                onClick={this.handleReset}
                style={{
                  padding: 'var(--button-padding-md)',
                  background: 'var(--brand-primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-medium)',
                  cursor: 'pointer',
                  transition: 'var(--transition-fast)',
                }}
                onMouseEnter={e =>
                  ((e.target as HTMLElement).style.background = 'var(--brand-primary-dark)')
                }
                onMouseLeave={e =>
                  ((e.target as HTMLElement).style.background = 'var(--brand-primary)')
                }
              >
                Try Again
              </button>
              
              <a
                href="/"
                style={{
                  padding: 'var(--button-padding-md)',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-medium)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-medium)',
                  textDecoration: 'none',
                  display: 'inline-block',
                  transition: 'var(--transition-fast)',
                }}
              >
                Go to Homepage
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
